import React, { useState } from 'react';
import { Button, Grid, Typography } from '@mui/material';
import { InviteAdminsDrawer, Page, Row, Spacer } from '../../components';
import { MutatorState, useAppStorage, useMutation, useObservable } from '@aesop-fables/scrinium';
import { capitalize, lowerCase } from 'lodash';
import {
  AdminTierTypeEnum,
  DrillDownWizard,
  drillDownWizardKey,
  drillDownTierToAdminTier,
} from '../../services/drillDown';
import { useService } from '@aesop-fables/containr-react';
import { UserData } from '../../models/UserData';
import { AdminCompartments, adminStorageKey } from '../../data/admin';
import { AddGroupAdmin, AddOrgAdmin, AddTeamAdmin } from '../../data/admin/mutations';
import AdminDataTable from '../../components/AdminDataTable';
import { UserCompartments, userStorageKey } from '../../data/user';
import { useAdminFilters } from '../../hooks/useAdminHooks';
import { BillingAdminManager } from '../payment/payment/BillingAdmin';

const AdminAccess: React.FC = () => {
  const appStorage = useAppStorage();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { filterAdmins } = useAdminFilters();

  const drillDownWizard = useService<DrillDownWizard>(drillDownWizardKey);
  const adminDataCache = appStorage.retrieve<AdminCompartments>(adminStorageKey);
  const userDataCache = appStorage.retrieve<UserCompartments>(userStorageKey);

  const tier = useObservable(drillDownWizard.tier$);
  const groupAdmins = useObservable(adminDataCache.observe$<UserData[]>('groupAdmins'));
  const teamAdmins = useObservable(adminDataCache.observe$<UserData[]>('teamAdmins'));
  const orgAdmins = useObservable(adminDataCache.observe$<UserData[]>('orgAdmins'));
  const currentUser = useObservable(userDataCache.observe$<UserData>('user'));

  const addGroupAdmin = useMutation(new AddGroupAdmin());
  const addTeamAdmin = useMutation(new AddTeamAdmin());
  const addOrgAdmin = useMutation(new AddOrgAdmin());

  const role = (tier && drillDownTierToAdminTier(tier)) ?? AdminTierTypeEnum.Organization;

  let admins: UserData[] | undefined;
  let addAdmin: MutatorState<UserData>;

  switch (role) {
    case AdminTierTypeEnum.Group:
      admins = groupAdmins;
      addAdmin = addGroupAdmin;
      break;
    case AdminTierTypeEnum.Team:
      admins = teamAdmins;
      addAdmin = addTeamAdmin;
      break;
    case AdminTierTypeEnum.Organization:
    default:
      admins = orgAdmins;
      addAdmin = addOrgAdmin;
      break;
  }

  const filteredAdmins = filterAdmins(admins, currentUser);

  return (
    <Page title={`${capitalize(role)} access`}>
      <Spacer height='xxs' />
      <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <Grid>
          <Typography variant='p20' color='secondary.main'>
            Give access to other people {role === AdminTierTypeEnum.Team ? 'on' : 'in'} your{' '}
            {lowerCase(role)}.
          </Typography>
          {role === AdminTierTypeEnum.Organization && (
            <>
              <Spacer height='xxs' />
              <BillingAdminManager admins={filteredAdmins} currentUser={currentUser} />
            </>
          )}
        </Grid>
        <Button onClick={() => setDrawerOpen(true)} color='primary'>
          Invite to {lowerCase(role)}
        </Button>
        <InviteAdminsDrawer
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
          role={role}
          addAdmin={addAdmin}
        />
      </Row>
      <Spacer height='sm' />
      <AdminDataTable
        role={role}
        usersList={filteredAdmins ?? []}
        currentUser={currentUser}
        loading={!filteredAdmins}
      />
    </Page>
  );
};

export default AdminAccess;
